// Who the condition report goes to (DD 22 Oct; #501): the job's contact in CRM
// unless another of the client's contacts is picked, or a new one is added to
// the client's account. Needs signal (the client's contacts are CRM's).
import { useCallback, useEffect, useState } from 'react';
import { Button, Input, Label } from '@higgins/ui';
import { ApiError, getReportContactsApi, isConnectionFailure, type ClientContactFromApi, type NewContactToApi } from '../lib/api';
import type { DeviceRecord } from '../lib/device-db';
import { IN_FIELD_ORDER, moveToNextField } from '../lib/field-order';
import { addReportContact, chooseReportContact } from '../lib/visit-lifecycle';

const JOB_CONTACT = '';
const BLANK: NewContactToApi = { firstName: '', lastName: '', jobTitle: '', email: '', phone: '' };
const NEW_FIELDS: { key: keyof NewContactToApi; label: string; type?: string }[] = [
  { key: 'firstName', label: 'First name' },
  { key: 'lastName', label: 'Last name' },
  { key: 'jobTitle', label: 'Job title' },
  { key: 'email', label: 'Email', type: 'email' },
  { key: 'phone', label: 'Phone', type: 'tel' },
];

interface Loaded {
  chosen: boolean;
  jobContactId: string | null;
  contacts: ClientContactFromApi[];
}

function describe(err: unknown): string {
  if (isConnectionFailure(err)) return 'Choosing the contact needs signal.';
  if (err instanceof ApiError) return err.message;
  return 'That didn’t work. Try again.';
}

const contactLabel = (c: ClientContactFromApi) => [c.name, c.jobTitle].filter(Boolean).join(', ');

export function ReportContact({ visit, online }: { visit: DeviceRecord; online: boolean }) {
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [adding, setAdding] = useState<NewContactToApi | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const load = useCallback(() => {
    getReportContactsApi(visit.id).then(setLoaded, (err) => setProblem(describe(err)));
  }, [visit.id]);
  useEffect(() => {
    if (online) load();
  }, [online, load]);

  async function run(work: () => Promise<void>) {
    setBusy(true);
    setProblem(null);
    try {
      await work();
      load();
    } catch (err) {
      // The client already has a contact with that email: pick them rather than make a second.
      // Picked: no error to show; only if picking them fails too is the original problem shown (#576 review).
      const existing = err instanceof ApiError && err.code === 'CONTACT_EXISTS' ? (err.body as { contact?: ClientContactFromApi }).contact : undefined;
      const picked = existing ? await chooseReportContact(visit, existing.id).then(() => true, () => false) : false;
      if (picked) {
        setAdding(null);
        load();
      } else setProblem(describe(err));
    } finally {
      setBusy(false);
    }
  }

  const chosenId = typeof visit.fields.hig_clientcontactid === 'string' ? visit.fields.hig_clientcontactid.toLowerCase() : JOB_CONTACT;
  const jobContact = loaded?.contacts.find((c) => c.id.toLowerCase() === loaded.jobContactId?.toLowerCase());

  return (
    <div>
      <Label htmlFor="report-contact">Report to</Label>
      {!online && !loaded && <p className="text-xs text-slate-500">The client&rsquo;s contacts need signal. Left as it is, the report goes to the job&rsquo;s contact.</p>}
      {loaded && (
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <select
            id="report-contact"
            className="min-h-11 min-w-64 flex-1 rounded-md border border-slate-300 px-2 text-sm"
            value={chosenId}
            disabled={busy}
            onChange={(e) => void run(() => chooseReportContact(visit, e.target.value || null))}
          >
            <option value={JOB_CONTACT}>{jobContact ? `${contactLabel(jobContact)} (the job’s contact)` : 'The job’s contact in CRM'}</option>
            {loaded.contacts
              .filter((c) => c.id.toLowerCase() !== loaded.jobContactId?.toLowerCase())
              .map((c) => (
                <option key={c.id} value={c.id.toLowerCase()}>{contactLabel(c)}</option>
              ))}
          </select>
          {!adding && (
            <Button variant="outline" size="sm" className="min-h-11" disabled={busy} onClick={() => setAdding(BLANK)}>
              Add contact
            </Button>
          )}
        </div>
      )}
      {adding && (
        <div className="mt-2 grid gap-2 rounded-md border border-slate-200 p-3 sm:grid-cols-2">
          {NEW_FIELDS.map((f) => (
            <div key={f.key}>
              <Label htmlFor={`new-contact-${f.key}`} className="text-xs text-slate-600">{f.label}</Label>
              <Input
                id={`new-contact-${f.key}`}
                type={f.type ?? 'text'}
                value={adding[f.key]}
                onChange={(e) => setAdding({ ...adding, [f.key]: e.target.value })}
                onKeyDown={(e) => moveToNextField(e, true)}
                enterKeyHint="next"
                {...IN_FIELD_ORDER}
              />
            </div>
          ))}
          <div className="flex items-end gap-2 sm:col-span-2">
            <Button size="sm" className="min-h-11" disabled={busy || !adding.lastName.trim() || !adding.jobTitle.trim()} onClick={() => void run(async () => {
              await addReportContact(visit, adding);
              setAdding(null);
            })}>
              Add to the client
            </Button>
            <Button variant="outline" size="sm" className="min-h-11" disabled={busy} onClick={() => setAdding(null)}>
              Cancel
            </Button>
            <span className="text-xs text-slate-500">Last name and job title are needed. The contact is added to the client&rsquo;s account in CRM.</span>
          </div>
        </div>
      )}
      {problem && <p className="mt-1 text-sm text-red-700">{problem}</p>}
    </div>
  );
}
